import React from 'react';
import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatQtyForDisplay } from '../utils/decimalInput';
import { isExpiredIssueSerial, IssueSerial } from '../hooks/useIssueSerials';

interface Props {
  serials: IssueSerial[];
  search: string;
  onSearchChange: (value: string) => void;
  onAdd: (serial: IssueSerial) => void;
  loading: boolean;
}

const IssueSerialsFreeTable: React.FC<Props> = ({
  serials,
  search,
  onSearchChange,
  onAdd,
  loading,
}) => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <section className="min-w-0 border border-gray-300 rounded overflow-hidden">
      <div className="p-3 border-b bg-gray-50">
        <h3 className="font-semibold mb-2">{t('issueSerials.freeTitle')}</h3>
        <label className="block text-xs mb-1" htmlFor="issue-serial-search">
          {t('issueSerials.serialNumber')}
        </label>
        <input
          id="issue-serial-search"
          ref={inputRef}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="w-full h-8 px-2 border rounded outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
        />
      </div>
      <div className="overflow-auto max-h-[48vh]">
        {loading ? (
          <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
        ) : serials.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="mx-auto mb-2 text-gray-300" />
            <p>{t('issueSerials.noStock')}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className="p-2 text-left">{t('issueSerials.serialNumber')}</th>
                <th className="p-2 text-right">{t('issueSerials.stock')}</th>
                <th className="p-2 text-center">{t('issueSerials.expiryDate')}</th>
              </tr>
            </thead>
            <tbody>
              {serials.map((serial) => {
                const expired = isExpiredIssueSerial(serial.deadline);
                return (
                  <tr
                    key={serial.serial}
                    tabIndex={expired ? -1 : 0}
                    onDoubleClick={() => onAdd(serial)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') onAdd(serial);
                    }}
                    className={expired
                      ? 'border-t text-red-500 bg-gray-100 cursor-not-allowed'
                      : 'border-t cursor-pointer hover:bg-blue-50 focus:bg-blue-50 focus:outline-none'}
                  >
                    <td className="p-2 font-mono">{serial.serial}</td>
                    <td className="p-2 text-right">
                      {formatQtyForDisplay(serial.quantity, { showZero: true })}
                    </td>
                    <td className="p-2 text-center">{serial.deadline || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

export default IssueSerialsFreeTable;
